/**
 * Sizing: the bar (MultiCameraTimeline) fits itself to ONE row by setting `data-tier` on its
 * `group/bar` element to wide / mid / narrow / tiny — the richest that fits its OWN width,
 * so it works full-width on the wall and beside the Recordings page's segment list alike. The
 * pieces here hide their extras with `group-data-[tier=…]/bar:` classes.
 *
 * The playback control bar's pieces — one set, used by the Recorded wall AND both dashboards'
 * Recordings page, so the two players look and behave the same:
 *
 *   ClockReadout · DateStepper · (TimeRangePicker) · [timeline's Select + duration] ···
 *   TransportCluster = [⏮ «30 «10] [▶] [10» 30» ⏭] [− 1× +]
 *
 * Plus the maths behind ⏮ / ⏭ (jumpPointsFor / jumpTarget) and the keyboard shortcuts
 * (usePlaybackKeys). Presentational: every piece takes values and callbacks, no state.
 */
import { useEffect, useRef } from 'react';
import { PlayIcon, PauseIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDoubleLeftIcon, ChevronDoubleRightIcon } from '@heroicons/react/24/solid';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { JumpPill, SpeedPill } from './PlaybackTransport';
import { Tip } from '../common/Tooltip';

const PILL = 'inline-flex h-7 shrink-0 items-center overflow-hidden rounded-lg border border-border divide-x divide-border';
const SEG_BASE = 'flex h-7 items-center justify-center px-1.5 transition-colors hover:bg-muted disabled:opacity-40 disabled:hover:bg-transparent';

/** Previous / next event lands this far before the moment, so it is not the first frame. */
export const JUMP_LEAD_MS = 2000;

/** The playhead's time, and the day it is on (or a status note in its place). */
export function ClockReadout({ timeMs, note, noteTone = 'muted' }: { timeMs: number; note?: string | null; noteTone?: 'muted' | 'warn' }) {
  const d = new Date(timeMs);
  const full = `${d.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}, ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`;
  return (
    <Tip label={note ? `${full} — ${note}` : `Playhead: ${full}`}>
    <div className="flex h-7 shrink-0 items-center gap-1.5 pl-1">
      <span className="font-mono text-[14px] font-semibold text-foreground tabular-nums">
        {d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
      </span>
      <span className={`text-[11px] ${note ? (noteTone === 'warn' ? 'text-amber-500' : 'text-muted-foreground') : 'hidden text-muted-foreground group-data-[tier=wide]/bar:inline'}`}>
        {note || d.toLocaleDateString([], { day: 'numeric', month: 'short' })}
      </span>
    </div>
    </Tip>
  );
}

/**
 * ‹ Sep 25 › — the arrows jump to the previous / next day with footage when those are known.
 * The middle is a button showing the day that opens the browser's calendar (a full native date
 * field is ~110px of "09/25/2026" the one-row bar cannot spare); the input itself stays in the
 * DOM, invisible, so the calendar and keyboard entry still come from the browser.
 */
export function DateStepper({ date, max, onChange, prevDate, nextDate }: {
  /** YYYY-MM-DD, local. */
  date: string;
  max?: string;
  onChange: (date: string) => void;
  prevDate?: string | null;
  nextDate?: string | null;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  // The arrows are the first thing a narrow bar drops — the calendar still picks any day.
  const seg = `${SEG_BASE} text-muted-foreground hover:text-foreground`;
  const arrowWrap = 'group-data-[tier=narrow]/bar:hidden group-data-[tier=tiny]/bar:hidden';
  const [y, m, d] = date.split('-').map(Number);
  const day = new Date(y, m - 1, d);
  const shift = (days: number) => {
    const t = new Date(y, m - 1, d + days);
    return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, '0')}-${String(t.getDate()).padStart(2, '0')}`;
  };
  const isToday = !!max && date === max;
  const openCalendar = () => {
    const el = inputRef.current;
    if (!el) return;
    try { el.showPicker(); } catch { el.focus(); el.click(); }
  };
  return (
    <div className={PILL}>
      <Tip label={prevDate ? `Previous day with footage (${prevDate})` : 'Previous day'} className={arrowWrap}>
        <button className={seg} aria-label="Previous day" onClick={() => onChange(prevDate || shift(-1))}>
          <ChevronLeftIcon className="h-3.5 w-3.5" />
        </button>
      </Tip>
      <Tip label={`${day.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} — pick a day`}>
      <span className="relative flex h-7">
        <button
          type="button"
          onClick={openCalendar}
          aria-label="Date"
          className={`${SEG_BASE} gap-1 px-2 text-[11px] font-medium text-foreground`}
        >
          <CalendarDaysIcon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="tabular-nums">{isToday ? 'Today' : day.toLocaleDateString([], { day: 'numeric', month: 'short' })}</span>
        </button>
        <input
          ref={inputRef}
          type="date"
          value={date}
          max={max}
          tabIndex={-1}
          aria-hidden="true"
          onChange={(e) => { if (/^\d{4}-\d{2}-\d{2}$/.test(e.target.value)) onChange(e.target.value); }}
          className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        />
      </span>
      </Tip>
      <Tip label={!!max && date >= max ? 'Already on today' : nextDate ? `Next day with footage (${nextDate})` : 'Next day'} className={arrowWrap}>
        <button className={seg} aria-label="Next day" onClick={() => onChange(nextDate || shift(1))} disabled={!!max && date >= max}>
          <ChevronRightIcon className="h-3.5 w-3.5" />
        </button>
      </Tip>
    </div>
  );
}

export interface TransportClusterProps {
  playing: boolean;
  onToggle: () => void;
  /** Nothing to play at all — every control off. */
  disabled?: boolean;
  /** Only the play button off (e.g. a stream is loading). */
  playDisabled?: boolean;
  onSkip: (deltaSec: number) => void;
  onPrevEvent: () => void;
  onNextEvent: () => void;
  /** What ⏮ / ⏭ step through — worded in their tooltips. */
  eventKind?: string;
  playbackRate: number;
  speeds: number[];
  onRateChange: (rate: number) => void;
}

/** [⏮ «30 «10] [▶] [10» 30» ⏭] [− 1× +] — every jump around play, back left and forward right. */
export function TransportCluster({
  playing, onToggle, disabled = false, playDisabled = false, onSkip, onPrevEvent, onNextEvent,
  eventKind = 'event', playbackRate, speeds, onRateChange,
}: TransportClusterProps) {
  // Same ink as the jump pill's own segments, so the event ends read as part of it. The
  // smallest bar drops them first ([ / ] still jump) — the hide sits on the tooltip wrapper.
  const evSeg = `${SEG_BASE} text-foreground`;
  const evWrap = 'group-data-[tier=tiny]/bar:hidden';
  const i = Math.max(0, speeds.indexOf(playbackRate));
  const faster = speeds[(i + 1) % speeds.length];
  const slower = speeds[(i - 1 + speeds.length) % speeds.length];
  return (
    <>
      <JumpPill
        compact
        side="back"
        onSkip={onSkip}
        disabled={disabled}
        start={(
          <Tip label={`Previous ${eventKind} ([)`} className={evWrap}>
            <button className={evSeg} aria-label={`Previous ${eventKind}`} onClick={onPrevEvent} disabled={disabled}>
              <ChevronDoubleLeftIcon className="h-3.5 w-3.5" />
            </button>
          </Tip>
        )}
      />
      <Tip label={disabled ? 'Nothing recorded in this range' : playing ? 'Pause (Space)' : 'Play (Space)'}>
        <button
          onClick={onToggle}
          disabled={disabled || playDisabled}
          aria-label={playing ? 'Pause' : 'Play'}
          className="flex h-7 w-8 shrink-0 items-center justify-center rounded-lg bg-brand text-white transition-colors hover:bg-brand-dark disabled:opacity-40"
        >
          {playing ? <PauseIcon className="h-4 w-4" /> : <PlayIcon className="h-4 w-4" />}
        </button>
      </Tip>
      <JumpPill
        compact
        side="forward"
        onSkip={onSkip}
        disabled={disabled}
        end={(
          <Tip label={`Next ${eventKind} (])`} className={evWrap}>
            <button className={evSeg} aria-label={`Next ${eventKind}`} onClick={onNextEvent} disabled={disabled}>
              <ChevronDoubleRightIcon className="h-3.5 w-3.5" />
            </button>
          </Tip>
        )}
      />
      <SpeedPill
        compact
        playbackRate={playbackRate}
        onSpeedUp={() => onRateChange(faster)}
        onSlowDown={() => onRateChange(slower)}
        slowerSpeed={slower}
        fasterSpeed={faster}
      />
    </>
  );
}

interface SegmentLike { start_time: string; end_time: string; trigger?: string }
interface TriggerLike { event_time: string; window_start?: string }

/**
 * Where "something happened", for ⏮ / ⏭: event + motion windows, and footage kept because of
 * one. A stretch with none of those (continuous cameras, no detections) falls back to where
 * footage resumes after a gap, so the buttons are never dead. One list per camera in.
 */
export function jumpPointsFor(
  segmentLists: (SegmentLike[] | undefined)[],
  triggerLists: (TriggerLike[] | undefined)[],
  rangeStartMs: number,
  rangeEndMs: number,
): { kind: 'event' | 'recording'; points: number[] } {
  const events: number[] = [];
  const resumes: number[] = [];
  for (const rows of triggerLists) {
    for (const t of rows || []) {
      const ms = new Date(t.window_start || t.event_time).getTime();
      if (Number.isFinite(ms)) events.push(ms);
    }
  }
  for (const list of segmentLists) {
    const segs = [...(list || [])].sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
    let lastEnd = -Infinity;
    let lastTrigger = '';
    for (const sg of segs) {
      const st = new Date(sg.start_time).getTime();
      const trig = sg.trigger || 'continuous';
      if (trig !== 'continuous' && (trig !== lastTrigger || st - lastEnd > 2000)) events.push(st);
      if (st - lastEnd > 2000) resumes.push(st);
      lastEnd = Math.max(lastEnd, new Date(sg.end_time).getTime());
      lastTrigger = trig;
    }
  }
  const pts = (events.length ? events : resumes).filter((ms) => ms >= rangeStartMs && ms < rangeEndMs);
  return { kind: events.length ? 'event' : 'recording', points: [...new Set(pts)].sort((a, b) => a - b) };
}

/**
 * Where ⏮ / ⏭ should put the playhead from `nowMs`, or null when there is nothing that way.
 * Measured from where a jump would have put the event (JUMP_LEAD_MS before it), so pressing
 * again moves on instead of finding the same one. Back from well inside an event returns to
 * its start; back from its start goes to the one before.
 */
export function jumpTarget(points: number[], nowMs: number, dir: 1 | -1, minMs = -Infinity): number | null {
  const at = nowMs + JUMP_LEAD_MS;
  const t = dir > 0 ? points.find((p) => p > at + 500) : [...points].reverse().find((p) => p < at - 3000);
  return t === undefined ? null : Math.max(minMs, t - JUMP_LEAD_MS);
}

/**
 * Where a day's footage starts and ends, as the time range picker's "HH:mm" (start floored,
 * end rounded up to the minute, both clamped to the day) — its Footage / Last 1h presets.
 * Null when the day has none. One list per camera in.
 */
export function footageRangeOf(segmentLists: (SegmentLike[] | undefined)[], date: string): { start: string; end: string } | null {
  const [y, m, d] = date.split('-').map(Number);
  const day0 = new Date(y, m - 1, d).getTime();
  const day1 = day0 + 86_400_000;
  let lo = Infinity;
  let hi = -Infinity;
  for (const list of segmentLists) {
    for (const s of list || []) {
      const st = new Date(s.start_time).getTime();
      const en = new Date(s.end_time).getTime();
      if (en <= day0 || st >= day1) continue;
      lo = Math.min(lo, Math.max(day0, st));
      hi = Math.max(hi, Math.min(day1 - 60_000, en));
    }
  }
  if (!(lo < hi)) return null;
  const hm = (ms: number) => { const t = new Date(ms); return `${String(t.getHours()).padStart(2, '0')}:${String(t.getMinutes()).padStart(2, '0')}`; };
  const end = hm(Math.min(day1 - 60_000, Math.ceil(hi / 60_000) * 60_000));
  const start = hm(Math.floor(lo / 60_000) * 60_000);
  return start < end ? { start, end } : null;
}

/** space = play/pause, ←/→ = 10 s (Shift 30 s), [ / ] = previous / next event. Ignored while typing. */
export function usePlaybackKeys(handlers: { toggle: () => void; skip: (deltaSec: number) => void; jump: (dir: 1 | -1) => void; enabled?: boolean }) {
  const ref = useRef(handlers);
  ref.current = handlers;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const h = ref.current;
      if (h.enabled === false || e.ctrlKey || e.metaKey || e.altKey) return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable)) return;
      // A popover (the time range picker) owns the keyboard while it is open.
      if (document.querySelector('[role="dialog"][aria-label="Pick a time range"]')) return;
      if (e.code === 'Space') { e.preventDefault(); h.toggle(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); h.skip(e.shiftKey ? -30 : -10); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); h.skip(e.shiftKey ? 30 : 10); }
      else if (e.key === '[') { e.preventDefault(); h.jump(-1); }
      else if (e.key === ']') { e.preventDefault(); h.jump(1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
}
